import api from 'lib/trpcClient';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { ChatMessage } from '../types';
import { ChatMessageRole } from 'further-types/lp/ai-agent';

type Params = {
  activeSessionId: string | null;
  selectedVehicleId: string | null;
  setMessages: Dispatch<SetStateAction<Array<ChatMessage>>>;
  onHydrated: () => void;
};

export const useChatHydration = ({
  activeSessionId,
  selectedVehicleId,
  setMessages,
  onHydrated,
}: Params) => {
  const hydratedSessionRef = useRef<string | null>(null);

  const chatHistoryQuery = api.aiAgent.getThreadMessages.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      sessionId: activeSessionId ?? '',
    },
    {
      enabled: Boolean(selectedVehicleId && activeSessionId),
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    const sessionId = activeSessionId;
    if (!sessionId || !chatHistoryQuery.data) return;
    if (hydratedSessionRef.current === sessionId) return;

    // Guard against a race on first send: the session is created mid-send,
    // enabling this query before the message is persisted. Without this, an
    // empty result would wipe the optimistically-added user message.
    if (chatHistoryQuery.data.messages.length === 0) return;

    hydratedSessionRef.current = sessionId;

    const hydratedMessages = chatHistoryQuery.data.messages.map(
      (message): ChatMessage => {
        if (message.role === ChatMessageRole.User) {
          return {
            role: ChatMessageRole.User,
            content: message.content,
            timestamp: message.createdAt,
          };
        }

        return {
          role: ChatMessageRole.Assistant,
          segments: message.segments,
          pendingInvoice: null,
          timestamp: message.createdAt,
        };
      },
    );

    setMessages(hydratedMessages);
    onHydrated();
  }, [activeSessionId, chatHistoryQuery.data, onHydrated, setMessages]);

  useEffect(() => {
    if (!activeSessionId) {
      hydratedSessionRef.current = null;
      setMessages([]);
    }
  }, [activeSessionId, setMessages]);
};
